import { useEffect, useMemo, useState } from "react";
import { useProject } from "./ProjectLayout";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { isVisible, isAnswered, formatValue } from "@/lib/discovery";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, MessageSquarePlus, Lightbulb, ChevronDown, StickyNote } from "lucide-react";

export default function Review() {
  const { project } = useProject();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const isConsultant = user?.role === "admin" || user?.role === "consultant";

  const load = () => api.get(`/projects/${project.id}/discovery`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [project.id]);

  const responses = data?.responses || {};

  // Customer view: questions needing attention
  const clarifications = useMemo(() => {
    if (!data) return [];
    const out = [];
    data.modules.forEach((m) => m.questions.forEach((q) => {
      const ann = data.annotations[q.key];
      if (ann?.clarification_required) out.push({ module: m.title, q, ann });
    }));
    return out;
  }, [data]);

  if (!data) return <div className="flex flex-1 items-center justify-center text-slate-400">Loading…</div>;

  if (!isConsultant) {
    return (
      <div className="px-6 py-8 lg:px-10">
        <div className="mx-auto max-w-3xl">
          <div className="flex items-center gap-2 text-amber-600"><AlertTriangle className="h-5 w-5" /><h2 className="font-heading text-xl font-bold text-slate-900">Questions Requiring Your Attention</h2></div>
          <p className="mt-1 text-sm text-slate-500">Your consultant has asked for clarification on the items below.</p>
          <div className="mt-6 space-y-3">
            {clarifications.length === 0 && <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-16 text-center text-slate-400">Nothing needs your attention right now. 🎉</div>}
            {clarifications.map(({ module, q, ann }) => (
              <div key={q.key} className="rounded-xl border border-amber-200 bg-amber-50/50 p-5">
                <div className="text-xs font-semibold uppercase tracking-wide text-amber-600">{module}</div>
                <div className="mt-1 font-heading text-sm font-bold text-slate-800">{q.text}</div>
                <div className="mt-2 rounded-lg bg-white p-3 text-sm text-slate-600"><span className="font-semibold text-amber-700">Consultant note: </span>{ann.note}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 py-8 lg:px-10">
      <div className="mx-auto max-w-4xl">
        <h2 className="font-heading text-xl font-bold text-slate-900">Consultant Review</h2>
        <p className="mt-1 text-sm text-slate-500">Review each captured answer, add notes, request clarification, suggest a Salesforce approach and validate.</p>

        <div className="mt-6 space-y-8">
          {data.modules.map((m) => {
            const answered = m.questions.filter((q) => isVisible(q, responses) && isAnswered(responses[q.key]) && q.type !== "info");
            if (answered.length === 0) return null;
            return (
              <div key={m.key}>
                <div className="mb-3 flex items-center gap-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-900 text-xs font-bold text-white">{m.code}</span>
                  <h3 className="font-heading text-base font-bold text-slate-800">{m.title}</h3>
                </div>
                <div className="space-y-3">
                  {answered.map((q) => (
                    <ReviewItem key={q.key} projectId={project.id} question={q} value={responses[q.key]} custNote={data.notes?.[q.key]} annotation={data.annotations[q.key]} onSaved={load} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ReviewItem({ projectId, question, value, custNote, annotation, onSaved }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(annotation?.note || "");
  const [rec, setRec] = useState(annotation?.recommendation || "");
  const [clar, setClar] = useState(annotation?.clarification_required || false);
  const [validated, setValidated] = useState(annotation?.validated || false);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/annotations`, { question_key: question.key, note, recommendation: rec, clarification_required: clar, validated });
      toast.success("Saved");
      onSaved();
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`review-item-${question.key}`} className={cn("rounded-xl border bg-white p-5", validated ? "border-emerald-200" : clar ? "border-amber-200" : "border-slate-200")}>
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-start justify-between text-left">
        <div className="flex-1">
          <div className="font-heading text-sm font-bold text-slate-800">{question.text}</div>
          <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Customer Response</div>
          <div className="mt-1 text-sm text-slate-700">{formatValue(question, value)}</div>
          {custNote && <div className="mt-2 flex items-start gap-1.5 rounded-lg bg-amber-50 p-2.5 text-xs text-amber-800"><StickyNote className="mt-0.5 h-3.5 w-3.5 shrink-0" /> <span><span className="font-semibold">Customer note: </span>{custNote}</span></div>}
        </div>
        <div className="ml-3 flex items-center gap-2">
          {validated && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
          {clar && <AlertTriangle className="h-4 w-4 text-amber-500" />}
          <ChevronDown className={cn("h-4 w-4 text-slate-400 transition-transform", open && "rotate-180")} />
        </div>
      </button>

      {open && (
        <div className="mt-4 space-y-4 border-t border-slate-100 pt-4">
          {question.consultant_guidance && (
            <div className="flex items-start gap-2 rounded-lg bg-sky-50 p-3 text-xs text-slate-600"><Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-sky-500" /><span><span className="font-semibold text-sky-700">Guidance: </span>{question.consultant_guidance}</span></div>
          )}
          <div>
            <label className="text-xs font-semibold text-slate-500">Consultant Note</label>
            <Textarea data-testid={`note-${question.key}`} value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 min-h-[70px] rounded-lg" placeholder="Add a note…" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Salesforce Recommendation</label>
            <Textarea data-testid={`rec-${question.key}`} value={rec} onChange={(e) => setRec(e.target.value)} className="mt-1 min-h-[70px] rounded-lg" placeholder="Suggested Salesforce approach…" />
          </div>
          <div className="flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-600">
              <Switch data-testid={`clar-${question.key}`} checked={clar} onCheckedChange={setClar} /> <span className="flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5 text-amber-500" /> Clarification Required</span>
            </label>
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-600">
              <Switch data-testid={`valid-${question.key}`} checked={validated} onCheckedChange={setValidated} /> <span className="flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> Validated</span>
            </label>
            <Button size="sm" data-testid={`save-annotation-${question.key}`} onClick={save} disabled={busy} className="ml-auto rounded-lg bg-slate-900 hover:bg-slate-800">
              <MessageSquarePlus className="mr-1.5 h-4 w-4" /> Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
